/**
 * What one line of a keeper roster says, worked out away from the markup.
 *
 * The declaration row is the densest thing in the app: a name, three kinds of chip, a franchise
 * control with three states and five reasons for being in the third, a cost that changes when the
 * tag goes on, and a list of picks the manager could escalate onto. All of that is decisions, and
 * decisions belong where they can be tested without a browser. The components below this file are
 * meant to be dull.
 *
 * Everything here is derived from the keeper build (`keepers-2026.json`) and the rulebook. Nothing
 * is stored, so a rules correction reprices the row rather than leaving a frozen answer on screen.
 */

/** The slice of a keeper-build row this module reads. Wider than it needs to be is fine. */
export interface KeeperSource {
  player: string;
  pos?: string;
  /** Farm origin — the year he was bought out of the farm auction. */
  farmYear?: number | null;
  farmBid?: number | null;
  /** Where the NFL took him, for a farm graduate. */
  nflRd?: number | null;
  nflOverall?: number | null;
  /** The NFL draft year, when the build knows it. */
  nflYear?: number | null;
  acq?: string;
  farm?: boolean;
  mandatory?: boolean;
  dropped?: boolean;
  firstDropBy?: string | null;
  firstDropLabel?: string | null;
  firstDropMethod?: string | null;
  sleeper?: boolean;
  /** Last season's fantasy draft, for a sleeper. */
  draftRound?: number | null;
  draftPick?: number | null;
  tagEligible?: boolean;
  tagReason?: string;
  taggedSeason?: number | null;
}

/* -------------------------------------------------------------------------- the name line */

export interface SplitName { surname: string; given: string }

/**
 * `HARRISON JR., Marvin` — surname first, because a manager scanning sixteen rows is looking for
 * a surname and the given names are noise until he has found it.
 *
 * The first token is the given name and EVERYTHING after it is the surname. That one rule handles
 * all four shapes this league's rosters actually contain, which is why it is not a list of
 * suffixes: `Travis Etienne Jr.` keeps its suffix on the surname, `Amon-Ra St. Brown` keeps its
 * two-word surname whole, and `Philadelphia` — a defence, which has no given name at all — comes
 * back as a surname on its own rather than as a given name with nothing to follow it.
 */
export function splitName(full: string): SplitName {
  const parts = full.trim().split(/\s+/);
  if (parts.length < 2) return { surname: parts[0] ?? '', given: '' };
  return { given: parts[0]!, surname: parts.slice(1).join(' ') };
}

/* ------------------------------------------------------------------------------ the chips */

export type ChipKind = 'farm' | 'dropped' | 'sleeper';

export interface StatusChip {
  kind: ChipKind;
  label: string;
  /** What the chip says when it is tapped. Never empty — a chip with nothing to say is not drawn. */
  callout: string;
  /**
   * Who did it, set apart from the facts above it.
   *
   * A separate field rather than a fourth line of `callout`, because it is drawn differently —
   * "by Tim" in bold under a rule — and a bubble that took one string could only have made it
   * bold by parsing its own text back out.
   */
  by?: string;
}

/**
 * The status chips this player carries, in the order they are drawn.
 *
 * A chip is only drawn when it has a fact behind it, and the fact IS the callout.
 *
 * THE FARM CHIP MEANS AN OBLIGATION, NOT A BIOGRAPHY. It appears only while a graduate is in his
 * mandatory rookie year (2013-III-1 §5) — the one season his manager has no choice about. A
 * player who came out of the farm system years ago and has been an ordinary keeper ever since
 * gets no chip: nothing about this season's decision turns on it.
 *
 * His farm history is not lost. It still bars him from the franchise tag for good (2025 P05 §4,
 * as the commissioner read it in 2025), and that is where it surfaces — in the callout above
 * `Can't Tag`, at the moment somebody tries to do the thing it prevents. A chip on every row of
 * every ex-farm player would be a permanent label answering a question nobody asked.
 */
export function statusChips(
  r: KeeperSource,
  state: { farmForfeited?: boolean; sleeperMinRound?: number; lastSeason?: number } = {},
): StatusChip[] {
  const out: StatusChip[] = [];

  if (r.mandatory) {
    out.push({ kind: 'farm', label: 'Farm', callout: farmCallout(r, state.farmForfeited === true) });
  }
  /*
   * DROPPED ONLY MATTERS TO A SLEEPER.
   *
   * Being dropped in-season costs one thing and one thing only: it disqualifies a player from the
   * sleeper reward (2017-II-2 §2 — "never dropped during the regular season"). For anyone drafted
   * inside round 10 there is no reward to lose, so the chip was a permanent label on eleven of
   * sixteen rows recording a fact with no consequence — and a chip on nearly every row stops
   * being read at all, which costs the two rows where it does mean something.
   *
   * `sleeperMinRound` is passed in rather than imported, so the domain layer stays free of the
   * league config and a rule change to the band moves one number.
   */
  if (r.dropped && wasSleeperEligible(r, state.sleeperMinRound ?? 10)) {
    out.push({
      kind: 'dropped',
      label: 'Dropped',
      callout: dropCallout(r, state.lastSeason),
      ...(r.firstDropBy ? { by: r.firstDropBy } : {}),
    });
  }
  if (r.sleeper) {
    out.push({ kind: 'sleeper', label: 'Sleeper', callout: sleeperCallout(r) });
  }
  return out;
}

function farmCallout(r: KeeperSource, forfeited: boolean): string {
  /*
   * A graduate the NFL cut keeps his chip — he is still the farm player he was — and the change
   * is told here rather than by swapping the chip for another one. The three facts a manager
   * needs, in the order he needs them: he is not on an NFL roster, he costs the slot and the tax
   * regardless (2019-II-5 §1), and carrying him is now up to him (2015-III-1).
   */
  if (forfeited) {
    const where = r.farmYear ? ` Farm ${r.farmYear}.` : '';
    return `Never made a 53-man roster.${where} The slot is gone and still taxed, `
      + 'but you no longer have to carry him — keep him or drop him.';
  }

  /*
   * Leads with the obligation, because that is what the chip is telling him: this one is not a
   * choice this year. The provenance follows, because the next question after "I have to keep
   * him" is "what did he cost me and is he any good".
   */
  const bought = r.farmYear ? `Mandatory rookie year · farm ${r.farmYear}` : 'Mandatory rookie year';
  const cost = r.farmBid != null ? ` for ₣${r.farmBid}` : '';
  /*
   * Where the NFL took him, which is the fact a manager is actually weighing — a farm player is a
   * bet on a rookie and the draft slot is the market's opinion of that bet. `nflRd` and
   * `nflOverall` are separate fields and either can be missing; say what is known and stop, rather
   * than inventing a round from an overall number and a guess at how long a round is.
   */
  const year = r.nflYear ? `, ${r.nflYear}` : '';
  if (r.nflRd && r.nflOverall) {
    return `${bought}${cost} · NFL round ${r.nflRd}, ${ordinal(r.nflOverall)} overall${year}`;
  }
  if (r.nflOverall) return `${bought}${cost} · ${ordinal(r.nflOverall)} overall in the NFL draft${year}`;
  if (r.nflRd) return `${bought}${cost} · a round ${r.nflRd} NFL pick`;
  return `${bought}${cost} · undrafted by the NFL`;
}

/**
 * Was there a sleeper reward for him to lose?
 *
 * Round 10 or later last season (2017-II-2 §2). A player with no recorded round cannot be shown
 * to have had one, and a chip is only drawn when there is a fact behind it — so no round means
 * no chip rather than a chip hedged with "possibly".
 */
export function wasSleeperEligible(r: KeeperSource, minRound: number): boolean {
  return typeof r.draftRound === 'number' && r.draftRound >= minRound;
}

function dropCallout(r: KeeperSource, lastSeason?: number): string {
  /*
   * THREE LINES, in the order the question is asked: where he was taken, when he was dropped, and
   * by whom. It used to be one sentence — "Dropped by Luke 10/13 · waivers" — which reads as
   * gossip rather than as the reason he cannot be a sleeper. The draft slot is the line that
   * makes the other two matter, so it goes first.
   */
  const lines: string[] = [];
  if (r.draftRound) {
    const slot = r.draftPick
      ? `${r.draftRound}.${String(r.draftPick).padStart(2, '0')}`
      : `Round ${r.draftRound}`;
    /*
      The FANTASY season he was drafted in, which is last season — not `nflYear`, which is the
      year the NFL took a farm graduate and is empty for everybody else. Passed in rather than
      derived here so the domain layer does not have to know what year it is.
    */
    lines.push(`${lastSeason ? `${lastSeason} ` : ''}Draft: ${slot}`);
  }
  lines.push(r.firstDropLabel ? `Dropped: ${r.firstDropLabel}` : 'Dropped last season');
  // Who did it is `by` on the chip, not a third line here — see `StatusChip`.
  return lines.join('\n');
}

function sleeperCallout(r: KeeperSource): string {
  if (r.draftRound && r.draftPick) {
    return `Sleeper reward · drafted ${r.draftRound}.${String(r.draftPick).padStart(2, '0')} last season`;
  }
  if (r.draftRound) return `Sleeper reward · a round ${r.draftRound} pick last season`;
  return 'Sleeper reward · the first keeper year carries no penalty';
}

function ordinal(n: number): string {
  const t = n % 100;
  if (t >= 11 && t <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
}

/* -------------------------------------------------------------------------- the tag control */

export type TagState =
  /** He could be tagged and is not. */
  | { state: 'available' }
  /** He is the one player this manager has tagged. */
  | { state: 'on' }
  /** He cannot be tagged, and this is the rule that says so. */
  | { state: 'blocked'; reason: string };

/**
 * Which of the three states the 78px slot is in.
 *
 * The reason for `blocked` comes from the keeper build, which computes it from 2025 P05 — five
 * separate bars, not the three the design brief listed: already franchised, farm origin (a
 * lifetime bar, per the commissioner's 2025 refusal of Luke's attempt on a graduate two years
 * out), UDFA, the sleeper reward already spent, and a round value outside 3–9. Rendering the
 * rule's own words is the only way the screen cannot drift from it.
 */
export function tagState(
  r: KeeperSource, taggedPlayer: string | null, refusal?: string | null,
): TagState {
  if (taggedPlayer === r.player) return { state: 'on' };
  /*
   * A REFUSAL IS A BLOCK, not an error message.
   *
   * The slate-level bars — one tag a season, and the three slots a tag costs — used to be
   * discovered by pressing the toggle and reading a sentence in amber that appeared between two
   * rows. Offering a control that cannot work and explaining afterwards is worse than not
   * offering it: the row already has a place to say "no, and here is why", and it is the same
   * grey pill every other ineligible row wears.
   */
  if (refusal) return { state: 'blocked', reason: refusal };
  if (r.tagEligible) return { state: 'available' };
  return {
    state: 'blocked',
    reason: r.tagReason?.trim() || 'He is not eligible for the franchise tag (2025 P05).',
  };
}

/**
 * Only one tag exists per season (2025 P05 §1), so every other row's control has to go quiet the
 * moment one is used — otherwise the screen offers ten managers a choice they do not have.
 */
export function tagOfferedElsewhere(taggedPlayer: string | null, player: string): boolean {
  return taggedPlayer !== null && taggedPlayer !== player;
}

/* --------------------------------------------------------------------------- escalation */

export interface EscalationOption {
  round: number;
  /** True for the round he would land on anyway — drawn as the current position, not a choice. */
  current: boolean;
  /**
   * The keeper already standing on that pick, if there is one.
   *
   * Occupied picks are OPTIONS, not omissions. Taking one is legal — it just costs the other man
   * his place, so the tile names him and the screen asks what should happen to him. Leaving them
   * out made the strip look as though a manager held three picks when he holds six.
   */
  takenBy?: string;
}

/**
 * Which picks a keeper can be charged to.
 *
 * 2015-III-7: a keeper may be charged to any pick the manager OWNS at or before his cost round.
 * Two halves of that matter and both have been got wrong here before:
 *
 *   OWNS. Not "round N exists" — the ledger. A manager who traded his 4th cannot pay with it, and
 *   round arithmetic cannot know that. `ownedRounds` comes from `picks2026.ownedRoundsFor`, which
 *   is the same derivation the draft board uses.
 *
 *   AT OR BEFORE. Escalation runs EARLIER, never later. Giving up a pick that comes sooner in the
 *   draft is what it costs to keep a man whose natural price is a later one. An earlier draft of
 *   the design had this backwards.
 *
 * A manager holding two picks in one round gets that round once — the choice is which round pays,
 * not which of two identical picks.
 */
export function escalationOptions(
  costRound: number,
  ownedRounds: readonly number[],
  landedOn?: number,
  occupied: ReadonlyMap<number, string> = new Map(),
): EscalationOption[] {
  /*
   * THE CAP IS WHAT HE ACTUALLY COSTS, not what the Grid would have charged.
   *
   * A franchise-tagged player is charged his own round rather than the escalated one (2025 P05
   * §2), and "at or before his cost round" then means at or before THAT. Capping on the Grid
   * figure offered a tagged keeper three picks when the rules allow him six — and it looked
   * right, because three plausible tiles is not a visibly empty strip.
   */
  const cap = Math.max(costRound, landedOn ?? 0);
  const seen = new Set<number>();
  const rounds: number[] = [];
  for (const r of ownedRounds) {
    if (r > cap || seen.has(r)) continue;
    seen.add(r);
    rounds.push(r);
  }
  rounds.sort((a, b) => a - b);
  return rounds.map((round) => {
    const takenBy = occupied.get(round);
    return {
      round,
      current: round === (landedOn ?? costRound),
      ...(takenBy ? { takenBy } : {}),
    };
  });
}

/* ------------------------------------------------------------------ what the slate is charged for */

/**
 * Who a slate is charged for, which stopped being the same as who is on it.
 *
 * A farm graduate the NFL cut is no longer compulsory — 2015-III-1 ends the obligation to carry
 * him in as many words, and his manager may untick him. What does NOT end is the cost: 2019-II-5
 * §1 takes the keeper slot, forbids filling it, and charges the luxury tax as though he had been
 * kept. So the tick answers "is he on my roster" and this answers "what does the slate cost", and
 * every number a manager decides on comes off this one.
 *
 * Getting this backwards would hand a manager a free keeper slot because the NFL cut his
 * prospect — a penalty that pays. It is one line, so it gets its own function and its own test.
 */
export function chargedFor<T extends { player: string; mandatory?: boolean }>(
  roster: readonly T[],
  picked: ReadonlySet<string>,
  forfeited: (player: string) => boolean,
): T[] {
  const on = roster.filter((r) => picked.has(r.player));
  const alsoCharged = roster.filter(
    (r) => r.mandatory === true && forfeited(r.player) && !picked.has(r.player),
  );
  return [...on, ...alsoCharged];
}

/**
 * Whether this row's checkbox is locked.
 *
 * Locked while the rookie year is compulsory; unlocked the moment a commissioner records that he
 * never made a 53. The manager can then keep him or not — the slot is spent either way.
 */
export function keeperLocked(
  r: { mandatory?: boolean }, forfeited: boolean,
): boolean {
  return r.mandatory === true && !forfeited;
}
